import React, { useState } from 'react';
import { FaFileAlt, FaCalendarAlt, FaUser, FaUsers, FaFileAlt as FaDoc, FaShieldAlt, FaCheckCircle } from 'react-icons/fa';

const LostRecordsForm = ({ setStep, onSubmit }) => {
  const [formData, setFormData] = useState({
    dateTimeDiscovered: '',
    dateTimeOccurred: '',
    discoveredBy: '',
    recordType: '',
    recordFormat: '',
    patientsAffected: '',
    phiTypes: [],
    lastKnownLocation: '',
    circumstances: '',
    searchEfforts: '',
    incidentDescription: '',
    immediateActions: '',
    lawEnforcementContacted: false,
    preventiveMeasures: '',
    reportedBy: '',
    reportedByTitle: '',
    reportedByEmail: '',
  });

  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  const phiTypeOptions = [
    'Names and addresses',
    'Social Security numbers',
    'Medical record numbers',
    'Account numbers',
    'Date of birth',
    'Telephone numbers',
    'Email addresses',
    'Medical diagnoses',
    'Treatment information',
    'Prescription information',
    'Lab results',
    'Insurance information',
    'Financial information',
    'Other identifying information'
  ];

  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    
    if (type === 'checkbox') {
      if (name === 'phiTypes') {
        setFormData(prev => ({
          ...prev,
          phiTypes: checked 
            ? [...prev.phiTypes, value]
            : prev.phiTypes.filter(type => type !== value)
        }));
      } else {
        setFormData(prev => ({
          ...prev,
          [name]: checked
        }));
      }
    } else {
      setFormData(prev => ({
        ...prev,
        [name]: value
      }));
    }
    
    if (errors[name]) {
      setErrors(prev => ({ ...prev, [name]: null }));
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await onSubmit({
        type: 'hipaa',
        subtype: 'lostRecords',
        data: formData,
        timestamp: new Date().toISOString()
      });
    } catch (error) {
      console.error('Error submitting form:', error);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 via-white to-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="mx-auto max-w-4xl">
        {/* Header */}
        <div className="mb-8 text-center">
          <div className="inline-flex size-16 items-center justify-center rounded-full bg-violet-100">
            <FaFileAlt className="size-8 text-violet-600" aria-hidden="true" />
          </div>
          <h1 className="mt-4 text-3xl font-bold tracking-tight text-gray-900 sm:text-4xl">
            Lost/Misplaced Records Report
          </h1>
          <p className="mt-2 text-base text-gray-600">
            Report lost or misplaced PHI records
          </p>
        </div>

        {/* Form Card */}
        <div className="overflow-hidden rounded-xl bg-white shadow-lg ring-1 ring-gray-900/5">
          <form onSubmit={handleSubmit}>
            <div className="space-y-12 px-6 py-8 sm:px-10 sm:py-10">
              {/* Incident Details */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaCalendarAlt className="size-5 text-gray-400" />
                  Incident Details
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  When and how the loss was discovered
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="dateTimeDiscovered" className="block text-sm/6 font-medium text-gray-900">
                      Date/Time Discovered <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateTimeDiscovered"
                        name="dateTimeDiscovered"
                        type="datetime-local"
                        value={formData.dateTimeDiscovered}
                        onChange={handleInputChange}
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.dateTimeDiscovered ? 'outline-red-500' : ''}`}
                      />
                      {errors.dateTimeDiscovered && <p className="mt-1 text-sm text-red-600">{errors.dateTimeDiscovered}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="dateTimeOccurred" className="block text-sm/6 font-medium text-gray-900">
                      Date/Time Lost (if known)
                    </label>
                    <div className="mt-2">
                      <input
                        id="dateTimeOccurred"
                        name="dateTimeOccurred"
                        type="datetime-local"
                        value={formData.dateTimeOccurred}
                        onChange={handleInputChange}
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="discoveredBy" className="block text-sm/6 font-medium text-gray-900">
                      Discovered By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="discoveredBy"
                        name="discoveredBy"
                        type="text"
                        value={formData.discoveredBy}
                        onChange={handleInputChange}
                        placeholder="Name and title of person who discovered the loss"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.discoveredBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.discoveredBy && <p className="mt-1 text-sm text-red-600">{errors.discoveredBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="recordType" className="block text-sm/6 font-medium text-gray-900">
                      Type of Records Lost <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <select
                        id="recordType"
                        name="recordType"
                        value={formData.recordType}
                        onChange={handleInputChange}
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.recordType ? 'outline-red-500' : ''}`}
                      >
                        <option value="">Select record type</option>
                        <option value="patient_charts">Patient Charts</option>
                        <option value="lab_results">Lab Results</option>
                        <option value="x_rays">X-rays/Imaging</option>
                        <option value="insurance_forms">Insurance Forms</option>
                        <option value="billing_records">Billing Records</option>
                        <option value="referral_forms">Referral Forms</option>
                        <option value="consent_forms">Consent Forms</option>
                        <option value="prescription_pads">Prescription Pads</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.recordType && <p className="mt-1 text-sm text-red-600">{errors.recordType}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="recordFormat" className="block text-sm/6 font-medium text-gray-900">
                      Record Format <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <select
                        id="recordFormat"
                        name="recordFormat"
                        value={formData.recordFormat}
                        onChange={handleInputChange}
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.recordFormat ? 'outline-red-500' : ''}`}
                      >
                        <option value="">Select format</option>
                        <option value="paper">Paper Records</option>
                        <option value="electronic_device">Electronic Device (laptop, tablet, USB)</option>
                        <option value="cd_dvd">CD/DVD</option>
                        <option value="flash_drive">Flash Drive</option>
                        <option value="hard_drive">Hard Drive</option>
                        <option value="other">Other</option>
                      </select>
                      {errors.recordFormat && <p className="mt-1 text-sm text-red-600">{errors.recordFormat}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Affected Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUsers className="size-5 text-gray-400" />
                  Affected Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Patients and PHI in lost records
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="patientsAffected" className="block text-sm/6 font-medium text-gray-900">
                      Number of Patients Affected <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="patientsAffected"
                        name="patientsAffected"
                        type="number"
                        value={formData.patientsAffected}
                        onChange={handleInputChange}
                        min="1"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.patientsAffected ? 'outline-red-500' : ''}`}
                      />
                      {errors.patientsAffected && <p className="mt-1 text-sm text-red-600">{errors.patientsAffected}</p>}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm/6 font-medium text-gray-900 mb-4">
                      Types of PHI in Lost Records <span className="text-red-500">*</span>
                    </label>
                    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
                      {phiTypeOptions.map((type, index) => (
                        <div key={index} className="flex gap-3">
                          <div className="flex h-6 shrink-0 items-center">
                            <div className="group grid size-4 grid-cols-1">
                              <input
                                type="checkbox"
                                name="phiTypes"
                                value={type}
                                checked={formData.phiTypes.includes(type)}
                                onChange={handleInputChange}
                                className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                              />
                              <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                                <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                              </svg>
                            </div>
                          </div>
                          <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">{type}</label>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="lastKnownLocation" className="block text-sm/6 font-medium text-gray-900">
                      Last Known Location
                    </label>
                    <div className="mt-2">
                      <input
                        id="lastKnownLocation"
                        name="lastKnownLocation"
                        type="text"
                        value={formData.lastKnownLocation}
                        onChange={handleInputChange}
                        placeholder="Where were the records last seen or known to be?"
                        className="block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Incident Description */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaFileAlt className="size-5 text-gray-400" />
                  Incident Description
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Detailed description of the loss
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="circumstances" className="block text-sm/6 font-medium text-gray-900">
                      Circumstances of Loss <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="circumstances"
                        name="circumstances"
                        rows={3}
                        value={formData.circumstances}
                        onChange={handleInputChange}
                        placeholder="Describe the circumstances under which the records were lost or misplaced..."
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.circumstances ? 'outline-red-500' : ''}`}
                      />
                      {errors.circumstances && <p className="mt-1 text-sm text-red-600">{errors.circumstances}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="searchEfforts" className="block text-sm/6 font-medium text-gray-900">
                      Search Efforts
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="searchEfforts"
                        name="searchEfforts"
                        rows={3}
                        value={formData.searchEfforts}
                        onChange={handleInputChange}
                        placeholder="Describe what efforts have been made to locate the missing records..."
                        className="block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6"
                      />
                    </div>
                  </div>

                  <div>
                    <label htmlFor="incidentDescription" className="block text-sm/6 font-medium text-gray-900">
                      Additional Details <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="incidentDescription"
                        name="incidentDescription"
                        rows={3}
                        value={formData.incidentDescription}
                        onChange={handleInputChange}
                        placeholder="Provide any additional details about the incident..."
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.incidentDescription ? 'outline-red-500' : ''}`}
                      />
                      {errors.incidentDescription && <p className="mt-1 text-sm text-red-600">{errors.incidentDescription}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Response Actions */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaShieldAlt className="size-5 text-gray-400" />
                  Response Actions
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Actions taken in response to the loss
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8">
                  <div>
                    <label htmlFor="immediateActions" className="block text-sm/6 font-medium text-gray-900">
                      Immediate Actions Taken <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="immediateActions"
                        name="immediateActions"
                        rows={3}
                        value={formData.immediateActions}
                        onChange={handleInputChange}
                        placeholder="What immediate actions were taken when the loss was discovered?"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.immediateActions ? 'outline-red-500' : ''}`}
                      />
                      {errors.immediateActions && <p className="mt-1 text-sm text-red-600">{errors.immediateActions}</p>}
                    </div>
                  </div>

                  <div className="flex gap-3">
                    <div className="flex h-6 shrink-0 items-center">
                      <div className="group grid size-4 grid-cols-1">
                        <input
                          type="checkbox"
                          name="lawEnforcementContacted"
                          checked={formData.lawEnforcementContacted}
                          onChange={handleInputChange}
                          className="col-start-1 row-start-1 appearance-none rounded border border-gray-300 bg-white checked:border-violet-600 checked:bg-violet-600 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600"
                        />
                        <svg fill="none" viewBox="0 0 14 14" className="pointer-events-none col-start-1 row-start-1 size-3.5 self-center justify-self-center stroke-white">
                          <path d="M3 8L6 11L11 3.5" strokeWidth={2} strokeLinecap="round" strokeLinejoin="round" className="opacity-0 group-has-[:checked]:opacity-100" />
                        </svg>
                      </div>
                    </div>
                    <label className="text-sm/6 font-medium text-gray-900 cursor-pointer">Law enforcement has been contacted</label>
                  </div>

                  <div>
                    <label htmlFor="preventiveMeasures" className="block text-sm/6 font-medium text-gray-900">
                      Preventive Measures <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <textarea
                        id="preventiveMeasures"
                        name="preventiveMeasures"
                        rows={3}
                        value={formData.preventiveMeasures}
                        onChange={handleInputChange}
                        placeholder="What measures will be implemented to prevent similar incidents?"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-2.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.preventiveMeasures ? 'outline-red-500' : ''}`}
                      />
                      {errors.preventiveMeasures && <p className="mt-1 text-sm text-red-600">{errors.preventiveMeasures}</p>}
                    </div>
                  </div>
                </div>
              </div>

              {/* Reporter Information */}
              <div className="border-b border-gray-900/10 pb-12">
                <h2 className="text-base/7 font-semibold text-gray-900 flex items-center gap-2">
                  <FaUser className="size-5 text-gray-400" />
                  Reporter Information
                </h2>
                <p className="mt-1 text-sm/6 text-gray-600">
                  Your contact information
                </p>

                <div className="mt-10 grid grid-cols-1 gap-x-6 gap-y-8 sm:grid-cols-2">
                  <div>
                    <label htmlFor="reportedBy" className="block text-sm/6 font-medium text-gray-900">
                      Reported By <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedBy"
                        name="reportedBy"
                        type="text"
                        value={formData.reportedBy}
                        onChange={handleInputChange}
                        placeholder="Full name"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedBy ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedBy && <p className="mt-1 text-sm text-red-600">{errors.reportedBy}</p>}
                    </div>
                  </div>

                  <div>
                    <label htmlFor="reportedByTitle" className="block text-sm/6 font-medium text-gray-900">
                      Title/Position <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByTitle"
                        name="reportedByTitle"
                        type="text"
                        value={formData.reportedByTitle}
                        onChange={handleInputChange}
                        placeholder="Job title or position"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByTitle ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByTitle && <p className="mt-1 text-sm text-red-600">{errors.reportedByTitle}</p>}
                    </div>
                  </div>

                  <div className="sm:col-span-2">
                    <label htmlFor="reportedByEmail" className="block text-sm/6 font-medium text-gray-900">
                      Email Address <span className="text-red-500">*</span>
                    </label>
                    <div className="mt-2">
                      <input
                        id="reportedByEmail"
                        name="reportedByEmail"
                        type="email"
                        value={formData.reportedByEmail}
                        onChange={handleInputChange}
                        placeholder="email@example.com"
                        required
                        className={`block w-full rounded-md bg-white px-3 py-1.5 text-base text-gray-900 outline outline-1 -outline-offset-1 outline-gray-300 placeholder:text-gray-400 focus:outline focus:outline-2 focus:-outline-offset-2 focus:outline-violet-600 sm:text-sm/6 ${errors.reportedByEmail ? 'outline-red-500' : ''}`}
                      />
                      {errors.reportedByEmail && <p className="mt-1 text-sm text-red-600">{errors.reportedByEmail}</p>}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Form Actions */}
            <div className="flex items-center justify-center gap-x-4 border-t border-gray-200 px-6 py-4 sm:px-10">
              <button
                type="submit"
                disabled={loading}
                className="inline-flex items-center gap-x-2 rounded-md bg-violet-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-violet-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-violet-600 disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
              >
                {loading ? (
                  <>
                    <svg className="animate-spin -ml-0.5 size-5" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span>Submitting...</span>
                  </>
                ) : (
                  <>
                    <FaCheckCircle aria-hidden="true" className="-ml-0.5 size-5" />
                    <span>Submit Report</span>
                  </>
                )}
              </button>
            </div>
          </form>
        </div>
      </div>
    </div>
  );
};

export default LostRecordsForm;
